import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Wand2, Save, RotateCcw, Sunrise, Sunset, Moon, Lock, Users, ChevronDown, ExternalLink } from "lucide-react";
import DateField from "@/components/DateField";
import { todayLocal } from "@/lib/timefmt";
import { taskStatus, STATUS_META } from "@/lib/taskStatus";
import { sunTimesLocal, localOffsetMin } from "@/lib/suntimes";

const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
};

const hm2min = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const min2hm = (v) => { v = ((Math.round(v) % 1440) + 1440) % 1440; return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`; };
const snap5 = (v) => Math.round(v / 5) * 5;
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const HEADER_H = 30;
const LANE_H = 80;
const BLOCK_H = 66;
const DAY_BG = "rgba(255,255,255,0.94)";
const TWILIGHT_BG = "rgba(203,213,225,0.92)";
const NIGHT_BG = "rgba(100,116,139,0.8)";
const OFFDUTY_BG = "rgba(100,116,139,0.45)";
const GRID_LINE = "rgba(71,85,105,0.35)";
const SEC_H = 20;

export default function TaskTimeline({ userId, companyId, locationId, refreshKey, onChanged, onQuickAdd, onEditTask, groupData = {}, tasksLabel = "Tasks", kindFilter = "all", endpoint = "/tasks" }) {
  const [groupBy, setGroupBy] = useState("none");
  const [anchor, setAnchor] = useState(todayLocal());
  const [days, setDays] = useState(1);
  const [pph, setPph] = useState(48);
  const [tasks, setTasks] = useState([]);
  const [duty, setDuty] = useState({});
  const [dutyEff, setDutyEff] = useState({});
  const [tzMode, setTzMode] = useState("local");
  const [location, setLocation] = useState(null);
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);
  const [showOrgCommits, setShowOrgCommits] = useState(false);
  const [orgTasks, setOrgTasks] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);
  const [roleDropOpen, setRoleDropOpen] = useState(false);
  const [autoMin, setAutoMin] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [, force] = useState(0);
  const dragRef = useRef(null);
  const ppm = pph / 60;
  const offsetMin = localOffsetMin(location);
  const shift = tzMode === "local" ? 0 : -offsetMin;
  const todayStr = todayLocal();
  const nowMin = new Date(now).getHours() * 60 + new Date(now).getMinutes();

  const dates = useMemo(() => Array.from({ length: days }, (_, i) => addDays(anchor, i)), [anchor, days]);

  const load = () => {
    api.get(`${endpoint}?from_date=${dates[0]}&to_date=${dates[dates.length - 1]}`).then((r) => setTasks(r.data)).catch(() => {});
    dates.forEach((d) => {
      api.get(`/duty/status?date=${d}&include_tasks=false`).then((r) => setDuty((m) => ({ ...m, [d]: r.data }))).catch(() => {});
      api.get(`/duty/status?date=${d}`).then((r) => setDutyEff((m) => ({ ...m, [d]: r.data }))).catch(() => {});
    });
  };
  useEffect(load, [dates[0], days, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [astro, setAstro] = useState(null);
  useEffect(() => {
    setAstro(null);
    if (!locationId) return;
    api.get(`/astronomy?date=${dates[0]}&location_id=${locationId}`).then((r) => { if (!r.data?.no_coords) setAstro(r.data); }).catch(() => {});
  }, [dates[0], locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOrg = () => {
    if (!showOrgCommits || !companyId) { setOrgTasks([]); return; }
    api.get(`/org/commitments?company_id=${companyId}&from_date=${dates[0]}&to_date=${dates[dates.length - 1]}`)
      .then((r) => setOrgTasks(r.data)).catch(() => {});
  };
  useEffect(loadOrg, [showOrgCommits, companyId, dates[0], days, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);
  useEffect(() => {
    if (!autoMin) return undefined;
    const id = setInterval(() => { load(); loadOrg(); }, autoMin * 60000);
    return () => clearInterval(id);
  }, [autoMin, showOrgCommits, companyId, dates[0], days, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const orgRoles = useMemo(
    () => [...new Set(orgTasks.flatMap((t) => (t.members || []).map((m) => m.role || "Member")))].sort(),
    [orgTasks]
  );

  useEffect(() => {
    if (!companyId || !locationId) { setLocation(null); return; }
    api.get(`/locations?company_id=${companyId}`).then((r) => {
      setLocation(r.data.find((l) => l.id === locationId) || null);
    }).catch(() => {});
  }, [companyId, locationId]);

  const curOf = (t) => {
    const pv = pending[t.id];
    if (pv) return { ...pv, dur: pv.end - pv.start };
    let dur = hm2min(t.end) - hm2min(t.start);
    if (dur <= 0) dur += 1440;
    if (t.end_date && t.end_date !== t.date) {
      const di = dates.indexOf(t.date);
      const ei = dates.indexOf(t.end_date);
      if (ei >= 0) dur = (ei - Math.max(di, 0)) * 1440 + hm2min(t.end) - (di >= 0 ? hm2min(t.start) : 0);
      else dur = (dates.length - Math.max(di, 0)) * 1440 - (di >= 0 ? hm2min(t.start) : 0); // end beyond the window — clamp at the last visible day
    }
    return { start: hm2min(t.start), end: hm2min(t.start) + dur, brief: t.brief_min || 0, debrief: t.debrief_min || 0, dur };
  };

  const sections = useMemo(() => {
    const items = tasks.map((t) => {
      let di = dates.indexOf(t.date);
      if (di < 0) {
        if (!t.end_date || dates.indexOf(t.end_date) < 0) return null;
        di = 0; // multi-day item that started before the window — clip to the first visible day
      }
      const live = dragRef.current?.id === t.id ? { ...dragRef.current.cur, dur: dragRef.current.cur.end - dragRef.current.cur.start } : null;
      const base = curOf(t);
      const c = live || base;
      const startMin = dates.indexOf(t.date) >= 0 ? base.start : 0;
      const s = di * 1440 + startMin - base.brief;
      const e = di * 1440 + startMin + base.dur + base.debrief;
      const cs = di * 1440 + (dates.indexOf(t.date) >= 0 ? c.start : 0) - c.brief;
      return { t, c, s, e, cs };
    }).filter(Boolean).sort((a, b) => a.s - b.s);
    const pack = (list) => {
      const out = [];
      for (const it of list) {
        const lane = out.find((ln) => ln.every((o) => o.e <= it.s || o.s >= it.e));
        if (lane) lane.push(it); else out.push([it]);
      }
      return out;
    };
    const equip = groupData.equipById || {};
    const orgOf = groupData.orgByUser || {};
    const BUCKETS = [[0, "Night 00–06"], [360, "Morning 06–12"], [720, "Afternoon 12–18"], [1080, "Evening 18–24"]];
    const keyOf = (it) => {
      if (groupBy === "type") return it.t.task_type_name;
      if (groupBy === "org") return orgOf[it.t.created_by] || "Other";
      if (groupBy === "start") { const m = it.c.start % 1440; return (BUCKETS.filter(([b]) => m >= b).pop() || BUCKETS[0])[1]; }
      if (groupBy === "equipType") { const ts = [...new Set((it.t.equipment_ids || []).map((id) => equip[id]?.type_name).filter(Boolean))]; return ts.length ? ts.join(" + ") : "No equipment"; }
      if (groupBy === "equipSub") { const ts = [...new Set((it.t.equipment_ids || []).map((id) => equip[id]?.subtype || equip[id]?.type_name).filter(Boolean))]; return ts.length ? ts.join(" + ") : "No equipment"; }
      if (groupBy === "location") return it.t.primary_location_name || "No location";
      return tasksLabel;
    };
    const sorties = kindFilter === "commitment" ? [] : items.filter((it) => it.t.kind !== "commitment");
    const commits = kindFilter === "sortie" ? [] : items.filter((it) => it.t.kind === "commitment");
    const bucketOrder = BUCKETS.map(([, l]) => l);
    const keys = [...new Set(sorties.map(keyOf))].sort((a, b) => groupBy === "start" ? bucketOrder.indexOf(a) - bucketOrder.indexOf(b) : a.localeCompare(b));
    const secs = keys.map((k) => ({ key: k, label: groupBy === "none" ? null : k, lanes: pack(sorties.filter((it) => keyOf(it) === k)) }));
    if (commits.length && kindFilter !== "commitment") secs.push({ key: "__commitments", label: groupBy === "none" ? null : "Commitments", lanes: pack(commits) });
    if (kindFilter === "commitment") {
      const ckeys = [...new Set(commits.map(keyOf))].sort((a, b) => groupBy === "start" ? bucketOrder.indexOf(a) - bucketOrder.indexOf(b) : a.localeCompare(b));
      for (const k of ckeys) secs.push({ key: `c-${k}`, label: groupBy === "none" ? null : k, lanes: pack(commits.filter((it) => keyOf(it) === k)) });
    }
    if (showOrgCommits && kindFilter !== "sortie") {
      const oc = [];
      for (const t of orgTasks) {
        const di = dates.indexOf(t.date);
        if (di < 0) continue;
        if (kindFilter === "commitment" && (t.created_by === userId || (t.member_ids || []).includes(userId))) continue; // own items already render in the main lanes
        let dur = hm2min(t.end) - hm2min(t.start);
        if (dur <= 0) dur += 1440;
        const c = { start: hm2min(t.start), end: hm2min(t.start) + dur, brief: t.brief_min || 0, debrief: t.debrief_min || 0, dur };
        const base = { t, c, s: di * 1440 + c.start - c.brief, e: di * 1440 + c.start + c.dur + c.debrief, cs: di * 1440 + c.start - c.brief, ro: true };
        for (const m of t.members || []) {
          if (roleFilter.length && !roleFilter.includes(m.role || "Member")) continue;
          oc.push({ ...base, member: m });
        }
      }
      const byMember = {};
      for (const it of oc) {
        const k = `${it.member.name || "Member"} · ${it.member.role || "Member"}`;
        (byMember[k] = byMember[k] || []).push(it);
      }
      Object.keys(byMember).sort().forEach((k) => secs.push({ key: `orgc-${k}`, label: k, lanes: pack(byMember[k]) }));
    }
    return secs;
  }, [tasks, dates, pending, dragRef.current?.cur, groupBy, groupData, kindFilter, showOrgCommits, orgTasks, roleFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const laidOut = useMemo(() => {
    let y = HEADER_H;
    let gi = 0;
    const rows = [];
    for (const sec of sections) {
      const startY = y;
      y += (sec.label ? SEC_H : 0) + Math.max(1, sec.lanes.length) * LANE_H;
      if (sec.label) rows.push({ type: "label", key: sec.key, label: sec.label, startY });
      sec.lanes.forEach((lane, li) => rows.push({ type: "lane", key: `${sec.key}-${li}`, lane, li, gi: gi++, startY, hasLabel: !!sec.label }));
    }
    return { rows, totalContentH: Math.max(LANE_H, y - HEADER_H) };
  }, [sections]);

  const payloadFor = (t, over = {}) => ({
    date: t.date, task_type_id: t.task_type_id,
    start: t.start, end: t.end, brief_min: t.brief_min ?? 0, debrief_min: t.debrief_min ?? 0,
    timezone: t.timezone, participant_ids: (t.participants || []).map((p) => p.user_id),
    mode_ids: t.mode_ids || [], sequence_ids: t.sequence_ids || [], equipment_ids: t.equipment_ids || [],
    end_date: t.end_date || null,
    comments: t.comments ?? null, airspace: t.airspace ?? null,
    end_location_id: t.end_location_id ?? null, auth_officer_id: t.auth_officer_id ?? null,
    ...over,
  });

  const beginDrag = (ev, it, kind) => {
    const st = taskStatus(it.t, pending, todayStr, nowMin);
    if (it.t.created_by !== userId || it.t.fixed || st === "cancelled" || st === "completed") return;
    if (ev.button === 1) return; // middle-drag pans the timeline
    ev.preventDefault();
    ev.stopPropagation();
    const startX = ev.clientX;
    const b = curOf(it.t);
    const orig = { start: b.start, end: b.end, brief: b.brief, debrief: b.debrief };
    dragRef.current = { id: it.t.id, cur: { ...orig } };
    const move = (e2) => {
      const d = snap5((e2.clientX - startX) / ppm);
      const c = { ...orig };
      if (kind === "move") { c.start += d; c.end += d; }
      else if (kind === "left") { c.start = Math.min(c.start + d, c.end - 5); }
      else if (kind === "right") { c.end = Math.max(c.end + d, c.start + 5); }
      else if (kind === "brief") { c.brief = Math.max(0, c.brief - d); }
      else if (kind === "debrief") { c.debrief = Math.max(0, c.debrief + d); }
      dragRef.current.cur = c;
      force((x) => x + 1);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const c = dragRef.current.cur;
      const changed = JSON.stringify(c) !== JSON.stringify(orig);
      dragRef.current = null;
      force((x) => x + 1);
      if (changed) setPending((p) => ({ ...p, [it.t.id]: c }));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const beginPan = (ev) => {
    if (ev.button !== 0 && ev.button !== 1) return;
    if (ev.target.closest("button")) return;
    const el = ev.currentTarget;
    const startX = ev.clientX;
    const startScroll = el.scrollLeft;
    let active = false;
    const move = (e2) => {
      const dx = e2.clientX - startX;
      if (!active && Math.abs(dx) < 4) return;
      active = true;
      el.scrollLeft = startScroll - dx;
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const saveChanges = async () => {
    const ids = Object.keys(pending);
    if (!ids.length) return;
    setSaving(true);
    try {
      let saved = 0;
      for (const id of ids) {
        const t = tasks.find((x) => x.id === id);
        const c = pending[id];
        if (!t) continue;
        await api.put(`${endpoint}/${id}`, payloadFor(t, {
          start: min2hm(c.start), end: min2hm(c.end), brief_min: c.brief, debrief_min: c.debrief,
        }));
        saved += 1;
      }
      toast.success(`Saved ${saved} task change(s)`);
      setPending({});
      onChanged?.();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const revertChanges = () => {
    setPending({});
    toast.success("Reverted to previous task times");
  };

  const optimise = () => {
    const next = { ...pending };
    let moved = 0, blocked = 0;
    const resOf = (t) => [t.created_by, ...(t.equipment_ids || []), ...(t.participants || []).map((p) => p.user_id)].filter(Boolean);
    for (const d of dates) {
      const du = duty[d];
      if (!du || !du.on_duty || du.off_work || !du.start || !du.end) continue;
      const dStart = hm2min(du.start);
      let dEnd = hm2min(du.end);
      if (dEnd <= dStart) dEnd += 1440;
      const active = (t) => t.date === d && t.status !== "cancelled" && taskStatus(t, null, todayStr, nowMin) !== "completed";
      const isMovable = (t) => active(t) && t.created_by === userId && !t.fixed;
      const info = (t) => {
        const c = curOf(t);
        return { t, start: c.start, dur: c.dur, brief: c.brief, debrief: c.debrief, total: c.dur + c.brief + c.debrief, res: resOf(t) };
      };
      const dayTasks = tasks.filter(active);
      const placed = dayTasks.filter((t) => !isMovable(t)).map((t) => {
        const x = info(t);
        return { s: x.start - x.brief, e: x.start + x.dur + x.debrief, res: x.res };
      });
      const movable = dayTasks.filter(isMovable).map(info).sort((a, b) => a.start - b.start);
      if (!movable.length) continue;
      const totalAll = movable.reduce((s, x) => s + x.total, 0);
      let cursor = dStart;
      if (totalAll > dEnd - dStart) cursor = dStart - 60; // permitted 1h early start
      const winEnd = dEnd + 60; // permitted 1h late finish
      for (const x of movable) {
        // earliest slot from cursor where no shared equipment/person overlaps an occupied interval
        let lo = cursor;
        let hit = true;
        while (hit) {
          hit = false;
          for (const p of placed) {
            if (!p.res.some((r) => x.res.includes(r))) continue;
            if (lo < p.e && lo + x.total > p.s) { lo = p.e; hit = true; }
          }
        }
        if (lo + x.total <= winEnd) {
          const ns = lo + x.brief;
          const ne = ns + x.dur;
          if (ns !== x.start) {
            next[x.t.id] = { start: ns, end: ne, brief: x.brief, debrief: x.debrief };
            moved += 1;
          }
          placed.push({ s: lo, e: lo + x.total, res: x.res });
          cursor = lo + x.total;
        } else {
          // no conflict-free slot in the duty window — keep current position and reserve its resources
          placed.push({ s: x.start - x.brief, e: x.start + x.dur + x.debrief, res: x.res });
          blocked += 1;
        }
      }
    }
    setPending(next);
    toast.success(moved
      ? `${moved} task(s) repositioned${blocked ? ` · ${blocked} left in place (no conflict-free slot)` : ""} — review, then Save changes or Revert`
      : blocked ? `No conflict-free slots for ${blocked} task(s)` : "Tasks already fit the duty day");
  };

  const width = days * 24 * pph;
  const tickStep = pph >= 50 ? 1 : pph >= 25 ? 2 : 4;
  const height = HEADER_H + laidOut.totalContentH + 26;
  const pendingCount = Object.keys(pending).length;

  return (
    <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 mb-6" data-testid="task-timeline">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="font-heading text-lg font-semibold">Timeline</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setAnchor(addDays(anchor, -1))} data-testid="timeline-prev" aria-label="Previous day"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors">
            <ChevronLeft className="h-4 w-4" /></button>
          <DateField value={anchor} onChange={setAnchor} testId="timeline-date" />
          <button onClick={() => setAnchor(addDays(anchor, 1))} data-testid="timeline-next" aria-label="Next day"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors">
            <ChevronRight className="h-4 w-4" /></button>
          <div className="flex rounded-md border border-slate-800 overflow-hidden">
            {[{ l: "Today", d: 1, id: "timeline-today" }, { l: "2 Days", d: 2, id: "timeline-2days" }, { l: "5 Days", d: 5, id: "timeline-5days" }].map((b) => (
              <button key={b.d} onClick={() => { setDays(b.d); if (b.d === 1) setAnchor(todayLocal()); }} data-testid={b.id}
                className={`px-3 py-1.5 text-xs transition-colors ${days === b.d ? "bg-blue-600/20 text-blue-300" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
                {b.l}
              </button>
            ))}
          </div>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="timeline-groupby"
            title="Group timeline rows"
            className="px-2 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs text-slate-300">
            <option value="none">No grouping</option>
            <option value="type">By task type</option>
            <option value="org">By org structure</option>
            <option value="start">By start time</option>
            <option value="equipType">By equipment type</option>
            <option value="equipSub">By equipment subtype</option>
            <option value="location">By location</option>
          </select>
          <button onClick={() => setPph((p) => Math.max(15, Math.round(p / 1.4)))} data-testid="timeline-zoom-out" aria-label="Zoom out"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors">
            <ZoomOut className="h-4 w-4" /></button>
          <button onClick={() => setPph((p) => Math.min(160, Math.round(p * 1.4)))} data-testid="timeline-zoom-in" aria-label="Zoom in"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors">
            <ZoomIn className="h-4 w-4" /></button>
          {kindFilter !== "sortie" && (
            <>
              <button onClick={() => setShowOrgCommits((v) => !v)} data-testid="timeline-org-commits"
                title="Overlay commitments from all members in your org tree"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors ${showOrgCommits ? "bg-emerald-600/20 text-emerald-300 border-emerald-600/50" : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white"}`}>
                <Users className="h-3.5 w-3.5" /> Org commitments
              </button>
              {showOrgCommits && (
                <div className="relative">
                  <button onClick={() => setRoleDropOpen((v) => !v)} data-testid="timeline-role-filter"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs text-slate-300 hover:text-white transition-colors">
                    Roles{roleFilter.length ? ` (${roleFilter.length})` : ""} <ChevronDown className="h-3 w-3" />
                  </button>
                  {roleDropOpen && (
                    <div className="absolute z-30 mt-1 right-0 w-44 rounded-md border border-slate-800 bg-slate-950 shadow-xl p-1" data-testid="timeline-role-menu">
                      {orgRoles.length === 0 && <div className="px-2 py-1.5 text-[11px] text-slate-500">No commitment roles in window</div>}
                      {orgRoles.map((r) => (
                        <label key={r} className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-900 rounded cursor-pointer" data-testid={`timeline-role-opt-${r}`}>
                          <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={roleFilter.includes(r)}
                            onChange={() => setRoleFilter((s) => s.includes(r) ? s.filter((x) => x !== r) : [...s, r])} />
                          {r}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <select value={tzMode} onChange={(e) => setTzMode(e.target.value)} data-testid="timeline-tz-select"
            title="Time display across the top of the timeline"
            className="px-2 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs text-slate-300">
            <option value="local">Local</option>
            <option value="utc">UTC</option>
            <option value="both">Local + UTC</option>
          </select>
          <select value={autoMin} onChange={(e) => setAutoMin(Number(e.target.value))} data-testid="timeline-autorefresh"
            title="Auto update interval"
            className="px-2 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs text-slate-300">
            <option value={0}>Manual</option>
            <option value={1}>Auto 1m</option>
            <option value={5}>Auto 5m</option>
            <option value={15}>Auto 15m</option>
            <option value={30}>Auto 30m</option>
          </select>
          <button onClick={() => window.open("/timeline", "_blank", "noopener,width=1500,height=850")} data-testid="timeline-popout"
            title="Open timeline in a new window"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors">
            <ExternalLink className="h-4 w-4" /></button>
          {kindFilter !== "commitment" && (
          <button onClick={optimise} disabled={saving} data-testid="timeline-optimise"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors disabled:opacity-50">
            <Wand2 className="h-3.5 w-3.5" /> Optimise tasks
          </button>
          )}
          {pendingCount > 0 && (
            <>
              <button onClick={saveChanges} disabled={saving} data-testid="timeline-save"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-500 text-white text-xs font-medium transition-colors disabled:opacity-50">
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : `Save changes (${pendingCount})`}
              </button>
              <button onClick={revertChanges} disabled={saving} data-testid="timeline-revert"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs transition-colors disabled:opacity-50">
                <RotateCcw className="h-3.5 w-3.5" /> Revert to previous
              </button>
            </>
          )}
        </div>
      </div>

      {astro && (
        <div className="flex items-center gap-x-5 gap-y-1 flex-wrap mb-2 px-3 py-2 rounded-md border border-slate-800 bg-slate-900/60 text-xs font-mono-num text-slate-300" data-testid="timeline-astro-row">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">{dayLabel(astro.date)} · {astro.location_name}</span>
          <span className="inline-flex items-center gap-1.5" data-testid="timeline-astro-sunrise"><Sunrise className="h-3.5 w-3.5 text-amber-400" /> Sunrise {astro.sunrise || "—"}</span>
          <span className="inline-flex items-center gap-1.5" data-testid="timeline-astro-sunset"><Sunset className="h-3.5 w-3.5 text-orange-400" /> Sunset {astro.sunset || "—"}</span>
          <span className="inline-flex items-center gap-1.5" data-testid="timeline-astro-moonrise"><Moon className="h-3.5 w-3.5 text-indigo-300" /> Moonrise {astro.moonrise || "—"}</span>
          <span className="inline-flex items-center gap-1.5" data-testid="timeline-astro-moonset"><Moon className="h-3.5 w-3.5 text-indigo-300" /> Moonset {astro.moonset || "—"}</span>
          <span data-testid="timeline-astro-moon-phase">Moon: {astro.moon_phase} · {astro.moon_illum}%</span>
        </div>
      )}

      <div className="overflow-auto max-h-[78vh] rounded-md border border-slate-800 cursor-grab active:cursor-grabbing" data-testid="timeline-scroll" onPointerDown={beginPan}>
        <div className="relative" style={{ width, height, minWidth: "100%" }} data-testid="timeline-canvas"
          onDoubleClick={(ev) => {
            if (!onQuickAdd || ev.target.closest('[data-testid^="timeline-task-"]')) return;
            const rect = ev.currentTarget.getBoundingClientRect();
            const total = (ev.clientX - rect.left) / ppm - shift;
            const di = Math.floor(total / 1440);
            if (di < 0 || di >= dates.length) return;
            const m = Math.max(0, Math.min(1439, snap5(total - di * 1440)));
            onQuickAdd(dates[di], min2hm(m));
          }}>
          <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800" style={{ height: HEADER_H, width }} data-testid="timeline-header">
            {dates.map((d, di) => {
              const x0 = di * 24 * pph;
              return (
                <div key={d}>
                  <div className="absolute top-0.5 text-[10px] font-mono-num uppercase tracking-widest text-slate-500" style={{ left: x0 + 6 }} data-testid={`timeline-daylabel-${d}`}>{dayLabel(d)}</div>
                  {Array.from({ length: Math.floor(24 / tickStep) + 1 }, (_, i) => i * tickStep).filter((h) => h < 24).map((h) => (
                    <div key={h} className="absolute text-[9px] font-mono-num text-slate-600 leading-none" style={{ left: x0 + h * pph + 2, top: 15 }}>
                      {tzMode === "both"
                        ? <>{min2hm(h * 60).slice(0, 2)}<br /><span className="text-slate-700">{min2hm(h * 60 - offsetMin).slice(0, 2)}z</span></>
                        : min2hm(h * 60 + shift).slice(0, 2)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {dates.map((d, di) => {
            const du = duty[d] || {};
            const loaded = d in duty;
            const dayOff = loaded && (du.off_work || du.on_duty === false);
            const x0 = di * 24 * pph;
            let ds = du.start ? hm2min(du.start) : null;
            let de = du.end ? hm2min(du.end) : null;
            if (ds != null && de != null && de <= ds) de += 1440;
            const showDuty = du.on_duty && !du.off_work && ds != null && de != null;
            return (
              <div key={d}>
                <div className="absolute top-0 bottom-0" data-testid={`timeline-day-${d}`}
                  style={{ left: x0, width: 24 * pph, background: DAY_BG }} />
                {dayOff && (
                  <div className="absolute top-1 right-auto text-[9px] font-mono-num uppercase tracking-widest text-slate-500"
                    data-testid={`timeline-offday-label-${d}`}
                    style={{ left: x0 + 24 * pph - 62 }}>Off duty</div>
                )}
                {showDuty && (
                  <div className="absolute top-0 bottom-0" data-testid={`timeline-duty-${d}`}
                    style={{ left: x0 + (ds + shift) * ppm, width: (de - ds) * ppm, background: "rgba(59,130,246,0.07)", borderLeft: "1px solid rgba(59,130,246,0.45)", borderRight: "1px solid rgba(59,130,246,0.45)" }} />
                )}
                {showDuty && (() => {
                  const eff = dutyEff[d];
                  if (!eff?.start || !eff?.end) return null;
                  let es = hm2min(eff.start);
                  let ee = hm2min(eff.end);
                  if (ee <= es) ee += 1440;
                  const segs = [];
                  if (es < ds) segs.push([es, ds]);
                  if (ee > de) segs.push([de, ee]);
                  return segs.map(([a, b], i) => (
                    <div key={i} className="absolute top-0 bottom-0" data-testid={`timeline-duty-ext-${d}-${i}`}
                      title="Duty extended by task times"
                      style={{ left: x0 + (a + shift) * ppm, width: (b - a) * ppm, background: "rgba(59,130,246,0.07)", borderLeft: "1px dashed rgba(59,130,246,0.6)", borderRight: "1px dashed rgba(59,130,246,0.6)" }} />
                  ));
                })()}
                {(() => {
                  const sun = location && location.lat != null && location.lng != null ? sunTimesLocal(d, location.lat, location.lng, offsetMin) : null;
                  if (!sun) return null;
                  const sr = sun.sunrise + shift;
                  const ss = sun.sunset + shift;
                  const nightAmEnd = Math.max(0, Math.min(1440, sr - 30));
                  const twAmEnd = Math.max(0, Math.min(1440, sr));
                  const twPmEnd = Math.max(0, Math.min(1440, ss + 30));
                  const stripTop = HEADER_H + laidOut.totalContentH;
                  return (
                    <>
                      <div className="absolute top-0 bottom-0" data-testid={`timeline-night-am-${d}`} title={`Night until ${min2hm(nightAmEnd)}`}
                        style={{ left: x0, width: nightAmEnd * ppm, background: NIGHT_BG }} />
                      {twAmEnd > nightAmEnd && (
                        <div className="absolute top-0 bottom-0" data-testid={`timeline-twilight-am-${d}`} title={`Twilight ${min2hm(nightAmEnd)}–${min2hm(twAmEnd)}`}
                          style={{ left: x0 + nightAmEnd * ppm, width: (twAmEnd - nightAmEnd) * ppm, background: TWILIGHT_BG }} />
                      )}
                      {twPmEnd > ss && (
                        <div className="absolute top-0 bottom-0" data-testid={`timeline-twilight-pm-${d}`} title={`Twilight ${min2hm(ss)}–${min2hm(twPmEnd)}`}
                          style={{ left: x0 + ss * ppm, width: (twPmEnd - ss) * ppm, background: TWILIGHT_BG }} />
                      )}
                      <div className="absolute top-0 bottom-0" data-testid={`timeline-night-pm-${d}`} title={`Night from ${min2hm(twPmEnd)}`}
                        style={{ left: x0 + twPmEnd * ppm, width: Math.max(0, 1440 - twPmEnd) * ppm, background: NIGHT_BG, borderLeft: "1px solid rgba(147,197,253,0.35)" }} />
                      <div className="absolute flex items-center gap-1 text-xs font-mono-num font-semibold text-amber-900 bg-amber-200/95 border border-amber-500 rounded px-1.5 py-0.5 shadow" data-testid={`timeline-sunrise-${d}`}
                        style={{ left: x0 + sr * ppm - 4, top: stripTop + 1 }} title={`Sunrise ${min2hm(sun.sunrise)} local`}>
                        <Sunrise className="h-3.5 w-3.5" />{min2hm(sun.sunrise)}
                      </div>
                      <div className="absolute flex items-center gap-1 text-xs font-mono-num font-semibold text-orange-900 bg-orange-200/95 border border-orange-500 rounded px-1.5 py-0.5 shadow" data-testid={`timeline-sunset-${d}`}
                        style={{ left: x0 + ss * ppm - 4, top: stripTop + 1 }} title={`Sunset ${min2hm(sun.sunset)} local`}>
                        <Sunset className="h-3.5 w-3.5" />{min2hm(sun.sunset)}
                      </div>
                    </>
                  );
                })()}
                {dayOff && (
                  <div className="absolute top-0 bottom-0" data-testid={`timeline-offduty-${d}`}
                    style={{ left: x0, width: 24 * pph, background: OFFDUTY_BG }} />
                )}
                <div className="absolute top-0 bottom-0 border-l border-slate-700/60" style={{ left: x0 }} />
                {Array.from({ length: Math.floor(24 / tickStep) + 1 }, (_, i) => i * tickStep).filter((h) => h < 24).map((h) => (
                  <div key={h} className="absolute" style={{ left: x0 + h * pph, top: HEADER_H, bottom: 0, borderLeft: `1px dashed ${GRID_LINE}` }} />
                ))}
              </div>
            );
          })}

          {laidOut.rows.map((row) => row.type === "label" ? (
            <div key={`label-${row.key}`} className="absolute left-0 right-0 flex items-center" data-testid={`timeline-group-${row.key}`}
              style={{ top: row.startY, height: SEC_H }}>
              <span className="text-[9px] uppercase tracking-widest font-mono-num text-slate-500 pl-2 shrink-0">{row.label}</span>
              <div className="flex-1 border-t border-slate-800/70 ml-2" />
            </div>
          ) : (
            <div key={`lane-${row.key}`} className="absolute left-0 right-0" data-testid={`timeline-lane-${row.gi}`}
              style={{ top: row.startY + (row.hasLabel ? SEC_H : 0) + row.li * LANE_H, height: LANE_H }}>
              {row.lane.map((it) => {
                const st = taskStatus(it.t, pending, todayStr, nowMin);
                const meta = STATUS_META[st];
                const own = it.t.created_by === userId;
                const draggable = own && !it.t.fixed && !it.t.end_date && st !== "cancelled" && st !== "completed";
                const left = (it.cs + shift) * ppm;
                const wB = it.c.brief * ppm;
                const wT = it.c.dur * ppm;
                const wD = it.c.debrief * ppm;
                const lblStart = min2hm(it.c.start);
                const lblEnd = min2hm(it.c.start + it.c.dur);
                return (
                  <div key={it.t.id} className="absolute" data-testid={`timeline-task-${it.t.id}`} data-status={st}
                    style={{ left, width: wB + wT + wD, top: 5, height: BLOCK_H, touchAction: "none" }}
                    title={`${it.t.task_type_name} ${lblStart}–${lblEnd} · brief ${it.c.brief}m · debrief ${it.c.debrief}m · ${meta.label}${it.t.fixed ? " · Fixed (not moveable)" : ""}${own ? "" : ` (logged by ${it.t.created_by_name})`}`}>
                    {it.c.brief > 0 && (
                      <div className="absolute top-0 bottom-0 rounded-l-sm" data-testid={`timeline-brief-${it.t.id}`}
                        style={{ left: 0, width: wB, background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.35) 0 4px, rgba(148,163,184,0.15) 4px 8px)" }} />
                    )}
                    <div className="absolute top-0 bottom-0 overflow-hidden text-[10px] select-none"
                      onPointerDown={draggable ? (e) => beginDrag(e, it, "move") : undefined}
                      onDoubleClick={own && onEditTask ? () => onEditTask(it.t) : undefined}
                      style={{
                        left: wB, width: wT, background: meta.block, cursor: draggable ? "grab" : "default",
                        borderRadius: it.c.brief > 0 || it.c.debrief > 0 ? 2 : 3,
                        borderLeft: it.t.color ? `3px solid ${it.t.color}` : undefined,
                      }}>
                      {wT > 90 ? (
                        <div className="h-full flex flex-col justify-between px-1.5 py-1 leading-tight text-white">
                          <span className={`truncate font-medium ${st === "cancelled" ? "line-through" : ""}`} data-testid={`timeline-task-name-${it.t.id}`}>
                            {it.t.fixed && <Lock className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5" />}
                            {it.t.kind === "commitment" ? <>{it.t.task_type_name}{it.t.name ? ` · ${it.t.name}` : ""}</> : (it.t.name || it.t.task_type_name)} <span className="opacity-80 font-mono-num">{lblStart}–{lblEnd}</span>
                          </span>
                          <span className="truncate opacity-80 italic" data-testid={`timeline-task-remarks-${it.t.id}`}>{it.t.remarks || " "}</span>
                          <span className="truncate" data-testid={`timeline-task-equip-${it.t.id}`}>
                            {(it.t.equipment_labels || []).join(", ")}
                            <span className="ml-1 opacity-90 font-semibold uppercase tracking-wide text-[8px]">{meta.label}</span>
                          </span>
                          <span className="truncate opacity-80" data-testid={`timeline-task-members-${it.t.id}`}>
                            {(it.t.participants || []).map((p) => p.name).join(", ") || " "}
                          </span>
                        </div>
                      ) : (
                        <div className="h-full flex items-center justify-center font-medium text-white">
                          {wT > 60 ? (
                            <span className={`truncate px-1 inline-flex items-center gap-1 ${st === "cancelled" ? "line-through" : ""}`}>{it.t.fixed && <Lock className="h-2.5 w-2.5 shrink-0" />}{it.t.kind === "commitment" ? it.t.task_type_name : (it.t.name || it.t.task_type_name)} {lblStart}–{lblEnd}</span>
                          ) : it.t.fixed ? (
                            <Lock className="h-2.5 w-2.5 shrink-0" />
                          ) : null}
                        </div>
                      )}
                    </div>
                    {it.c.debrief > 0 && (
                      <div className="absolute top-0 bottom-0 rounded-r-sm" data-testid={`timeline-debrief-${it.t.id}`}
                        style={{ left: wB + wT, width: wD, background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.35) 0 4px, rgba(148,163,184,0.15) 4px 8px)" }} />
                    )}
                    {draggable && (
                      <>
                        <div onPointerDown={(e) => beginDrag(e, it, "brief")} data-testid={`timeline-brief-handle-${it.t.id}`}
                          className="absolute top-0 bottom-0" style={{ left: 0, width: 7, cursor: "ew-resize", touchAction: "none" }} />
                        <div onPointerDown={(e) => beginDrag(e, it, "left")} data-testid={`timeline-left-${it.t.id}`}
                          className="absolute top-0 bottom-0" style={{ left: wB === 0 ? 7 : Math.max(wB - 3, 0), width: 7, cursor: "ew-resize", touchAction: "none" }} />
                        <div onPointerDown={(e) => beginDrag(e, it, "right")} data-testid={`timeline-right-${it.t.id}`}
                          className="absolute top-0 bottom-0" style={{ left: Math.max(wD === 0 ? wB + wT - 7 : Math.min(wB + wT - 4, wB + wT + wD - 7), 0), width: 7, cursor: "ew-resize", touchAction: "none" }} />
                        <div onPointerDown={(e) => beginDrag(e, it, "debrief")} data-testid={`timeline-debrief-handle-${it.t.id}`}
                          className="absolute top-0 bottom-0" style={{ left: Math.max(wD === 0 ? wB + wT + wD - 14 : wB + wT + wD - 7, 0), width: 7, cursor: "ew-resize", touchAction: "none" }} />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {sections.every((s) => !s.lanes.length) && (
            <div className="absolute text-xs text-slate-500" style={{ top: HEADER_H + 10, left: 12 }} data-testid="timeline-empty">
              No tasks in this window — log one below.
            </div>
          )}
          {(() => {
            const di = dates.indexOf(todayStr);
            if (di < 0) return null;
            const x = (di * 1440 + nowMin + shift) * ppm;
            return (
              <div className="absolute top-0 bottom-0 z-10 pointer-events-none" data-testid="timeline-now-line"
                style={{ left: x, borderLeft: "2px solid rgba(239,68,68,0.85)" }}>
                <div className="absolute -left-3 top-8 text-[8px] font-mono-num text-red-400 uppercase tracking-widest">now</div>
              </div>
            );
          })()}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px] font-mono-num text-slate-500 flex-wrap">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5" data-testid={`timeline-legend-${k}`}>
            <span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: m.block }} /> {m.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.5) 0 3px, rgba(148,163,184,0.2) 3px 6px)" }} /> Brief / Debrief</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4 border border-slate-600" style={{ background: DAY_BG }} /> Day</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4" style={{ background: TWILIGHT_BG }} /> Twilight</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4" style={{ background: NIGHT_BG }} /> Night</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4" style={{ background: OFFDUTY_BG }} /> Off duty</span>
        {!location && <span className="text-amber-400/80">Set your location in Profile → Preferences for sunrise/sunset</span>}
        <span className="ml-auto">Double-click empty space to add · double-click a task to edit · drag to move · edges resize · drag background to slide · Save changes or Revert to previous{tzMode === "utc" ? " · times in UTC" : ""}</span>
      </div>
    </div>
  );
}
